import { formatCompactNumber } from '@/helpers/Number';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { OfferType, PropertyTypeNumber } from '@/models/valuationFunnel';
import { PropertyType } from '@/types';
import { hgNumber } from '@smg-real-estate/domus';
import { HgSupportedLanguage } from '@smg-real-estate/domus/types';

export interface GroupedPropertyData {
  type: OfferType;
  items: PropertyTileData[];
  isFold: boolean;
}
export interface PropertyTileData {
  id: string;
  street: string;
  shortAddress: string;
  offerType: OfferType;
  propertyType: PropertyType;
  medianPrice: string;
  priceRange: {
    from: string;
    to: string;
  };
  room: number;
  livingSpace: string;
  geo: {
    lat: number;
    lon: number;
  };
  appraisalChangeRate?: number;
  hasNotEngaged?: boolean;
}

const getTileData = (
  data: PropertyValuationResult[],
  locale: HgSupportedLanguage,
): PropertyTileData[] => {
  return data.map((item) => ({
    id: item.leadId,
    street: item.street,
    shortAddress: [item.zipCode, item.town].join(' '),
    offerType: item.offerType,
    propertyType:
      item.propertyType === PropertyTypeNumber.Apartment
        ? PropertyType.Apartment
        : PropertyType.House,
    medianPrice: formatCompactNumber(item.appraisalPrice, locale),
    priceRange: {
      from: hgNumber(item.evaluatedPriceFrom, locale),
      to: hgNumber(item.evaluatedPriceTo, locale),
    },
    room: item.room,
    livingSpace: hgNumber(item.livingSpace, locale),
    geo: {
      lat: item.lat,
      lon: item.lon,
    },
    appraisalChangeRate: item.appraisalChangeRate,
    hasNotEngaged: item.hasNotEngaged,
  }));
};

export const adaptData = (
  data: PropertyValuationResult[],
  propertySize = 3,
  locale: HgSupportedLanguage,
): GroupedPropertyData[] =>
  [OfferType.Own, OfferType.Buying, OfferType.Other].map((offerType: OfferType) => {
    const properties = data.filter((item) => {
      // For Own type, include both Own and Selling properties
      if (offerType === OfferType.Own) {
        return item.offerType === OfferType.Own || item.offerType === OfferType.Selling;
      }
      return item.offerType === offerType;
    });
    return {
      type: offerType,
      items: getTileData(properties, locale),
      isFold: properties.length > propertySize,
    };
  });
